import { initializeApp, getApps, getApp, type FirebaseApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, type Auth } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getStorage, type FirebaseStorage } from "firebase/storage";
import { getFunctions, type Functions } from "firebase/functions";

// Build-safe Firebase configuration
interface FirebaseConfig {
  apiKey: string | undefined;
  authDomain: string | undefined;
  projectId: string | undefined;
  storageBucket: string | undefined;
  messagingSenderId: string | undefined;
  appId: string | undefined;
}

const getFirebaseConfig = (): FirebaseConfig => ({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
});

// Validate Firebase config only when needed
const validateFirebaseConfig = (config: FirebaseConfig) => {
  const requiredFields: (keyof FirebaseConfig)[] = [
    "apiKey",
    "authDomain",
    "projectId",
    "storageBucket",
    "messagingSenderId",
    "appId",
  ];

  for (const field of requiredFields) {
    if (!config[field]) {
      throw new Error(
        `Missing Firebase configuration: VITE_FIREBASE_${field.toUpperCase()}`
      );
    }
  }
};

// Lazy initialization to avoid build-time issues
let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let firebaseDb: Firestore | null = null;
let firebaseStorage: FirebaseStorage | null = null;
let firebaseFunctions: Functions | null = null;
let firebaseGoogleProvider: GoogleAuthProvider | null = null;

const initializeFirebase = (): FirebaseApp | null => {
  if (firebaseApp) return firebaseApp;

  try {
    const config = getFirebaseConfig();

    // Only validate if not in build/static generation context
    if (
      typeof window !== "undefined" ||
      import.meta.env.MODE === "development"
    ) {
      validateFirebaseConfig(config);
    }

    firebaseApp = !getApps().length ? initializeApp(config) : getApp();
    firebaseAuth = getAuth(firebaseApp);
    firebaseDb = getFirestore(firebaseApp);
    firebaseStorage = getStorage(firebaseApp);
    firebaseFunctions = getFunctions(firebaseApp);
    firebaseGoogleProvider = new GoogleAuthProvider();

    firebaseGoogleProvider.setCustomParameters({ prompt: "select_account" });

    return firebaseApp;
  } catch (error) {
    console.error("Failed to initialize Firebase:", error);

    // Return null during build/static generation to avoid crashes
    return null;
  }
};

// Export getters that initialize Firebase lazily
export const getFirebaseApp = (): FirebaseApp | null => {
  return initializeFirebase();
};

export const getFirebaseAuth = (): Auth | null => {
  if (firebaseAuth) return firebaseAuth;
  initializeFirebase();
  return firebaseAuth;
};

export const getFirebaseDB = (): Firestore | null => {
  if (firebaseDb) return firebaseDb;
  initializeFirebase();
  return firebaseDb;
};

export const getFirebaseStorage = () => {
  if (firebaseStorage) return firebaseStorage;
  initializeFirebase();
  return firebaseStorage;
};

export const getFirebaseFunctions = (): Functions | null => {
  if (firebaseFunctions) return firebaseFunctions;
  initializeFirebase();
  return firebaseFunctions;
};

export const getFirebaseGoogleProvider = (): GoogleAuthProvider | null => {
  if (firebaseGoogleProvider) return firebaseGoogleProvider;
  initializeFirebase();
  return firebaseGoogleProvider;
};

// Convenience exports - these initialize on first access
export { getFirebaseDB as db, getFirebaseFunctions as functions };

export const initFirebase = () => initializeFirebase();

if (import.meta.env.MODE === "development") {
  // Initialize Firebase in development to establish emulator connections
  initializeFirebase();
}

export default firebaseApp;
